import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { ChangeSiteHeadConfirmDialog } from '@/components/sites/ChangeSiteHeadConfirmDialog';
import { SiteForm, type SiteFormValues } from '@/components/sites/SiteForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { getSiteRequest, updateSiteRequest } from '@/services/sites';
import type { PaginatedResult } from '@/types/roles';
import type { SiteDetail, UpdateSiteBody } from '@/types/sites';
import type { UserListItem } from '@/types/users';

interface PendingSubmit {
  readonly values: SiteFormValues;
  readonly newSiteHeadName: string | null;
}

export function SiteEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingSubmit | null>(null);

  const detailQuery = useQuery<SiteDetail, ApiError>({
    queryKey: ['site', id],
    queryFn: () => getSiteRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5 * 60_000,
  });

  // Used to resolve the new site-head's display name when the dialog opens.
  // Sourced from the cache that SiteForm populates via its chained query.
  const siteHeadsListing = queryClient
    .getQueriesData<PaginatedResult<UserListItem>>({ queryKey: ['users', 'siteHeads'] })
    .map(([, data]) => data)
    .find((d) => d !== undefined);

  const updateMutation = useMutation<SiteDetail, ApiError, { body: UpdateSiteBody }>({
    mutationFn: ({ body }) => updateSiteRequest(id, body),
    onSuccess: (updated) => {
      toast.success(`Site '${updated.name}' saved`);
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['site', id] });
      void navigate(`/sites/${String(id)}`);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not update site', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('sites.update')) {
    return <Navigate to="/sites" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/sites" replace />;
  }

  function buildBody(values: SiteFormValues): UpdateSiteBody {
    return {
      name: values.name,
      code: values.code,
      location: values.location,
      address: values.address ?? null,
      description: values.description ?? null,
      siteHeadUserId: values.siteHeadUserId ?? null,
      isActive: values.isActive,
    };
  }

  function fireUpdate(values: SiteFormValues): void {
    updateMutation.mutate({ body: buildBody(values) });
  }

  function handleSubmit(values: SiteFormValues): void {
    setErrorMessage(null);
    const previousId = detailQuery.data?.siteHead?.id ?? null;
    const nextId = values.siteHeadUserId ?? null;
    if (previousId !== nextId) {
      // Resolve the new head's name from the cached site-heads listing.
      let newName: string | null = null;
      if (nextId !== null) {
        const found = siteHeadsListing?.items.find((u) => u.id === nextId);
        newName = found?.fullName ?? `User #${String(nextId)}`;
      }
      setPending({ values, newSiteHeadName: newName });
      return;
    }
    fireUpdate(values);
  }

  function handleDialogConfirm(): void {
    if (pending !== null) {
      const captured = pending;
      setPending(null);
      fireUpdate(captured.values);
    }
  }

  function handleDialogCancel(): void {
    setPending(null);
  }

  const initialValues: Partial<SiteFormValues> | undefined =
    detailQuery.data !== undefined
      ? {
          name: detailQuery.data.name,
          code: detailQuery.data.code,
          location: detailQuery.data.location,
          address: detailQuery.data.address ?? '',
          description: detailQuery.data.description ?? '',
          siteHeadUserId: detailQuery.data.siteHead?.id ?? null,
          isActive: detailQuery.data.isActive,
        }
      : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Site</h1>
        <p className="text-muted-foreground">Update site details and Site Head assignment.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{detailQuery.data?.name ?? 'Site'}</CardTitle>
          <CardDescription>Code must remain unique across the platform.</CardDescription>
        </CardHeader>
        <CardContent>
          {detailQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="site-edit-loading">
              Loading site…
            </p>
          )}
          {detailQuery.isError && (
            <p className="text-sm text-destructive" data-testid="site-edit-error">
              Failed to load site.
            </p>
          )}
          {detailQuery.data && initialValues !== undefined && (
            <SiteForm
              mode="edit"
              initialValues={initialValues}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              errorMessage={errorMessage}
            />
          )}
        </CardContent>
      </Card>

      <ChangeSiteHeadConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) handleDialogCancel();
        }}
        previousSiteHead={detailQuery.data?.siteHead ?? null}
        newSiteHeadName={pending?.newSiteHeadName ?? null}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}
